import React, { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { Link } from 'react-router-dom';
import { Box, Button, Chip, Tooltip } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { Edit, Hail, Person, Title } from '@mui/icons-material';
import { typeEmail, typePhone } from 'components/mui/Formatters';
import { PartyModel, PartyType } from 'models/PartyModel';
import { CompanyType } from 'models/CompanyModel';
import usePartyModals from 'modules/parties/hooks/usePartyModals';
import useParties from 'modules/parties/hooks/useParties';
import auth from 'recoil/atoms/auth';
import { removeItemAtIndex, replaceItemAtIndex } from 'utils/common';


type TableProps = {
    type: string,
    id?: number | null,
}

const TableInner = ({type, id}: TableProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const [parties, setParties] = useState<PartyModel[]>([]);
    const {getParties} = useParties();
    const {
        openEditPartyModal,
        openChangePartyTypeModal,
        openMergePartiesModal,
        openInviteManagerModal,
    } = usePartyModals();
    const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);

    const load = () => {
        getParties(type)
            .then(res => setParties(res.data.data));
    }

    // eslint-disable-next-line
    useEffect(load, [type]);

    const canEdit = !(type === PartyType.Dealer && my.type === CompanyType.Shipper);

    const columns: GridColDef<PartyModel>[] = [
        {
            field: 'id',
            headerName: '#',
            type: 'number',
            width: 30,
        },
        {
            field: 'name',
            headerName: t('name'),
            valueGetter: params => `${params.row.lastName || ''} ${params.row.firstName || ''} ${params.row.middleName || ''}`,
            width: 200,
        },
        {
            field: 'companyName',
            headerName: t('company'),
            valueGetter: params => `${params.row.companyName || ''}` + (params.row.companyCode ? ` (${params.row.companyCode})` : ''),
            width: 200,
        },
        {
            field: 'email',
            headerName: t('email'),
            ...typeEmail,
            width: 200,
        },
        {
            field: 'phone',
            headerName: t('phone'),
            ...typePhone,
            width: 200,
        },
        {
            field: 'address',
            headerName: t('address'),
            flex: 1,
        },
        ...my.type === CompanyType.Master ? [{
            field: 'manager',
            headerName: t('manager'),
            width: 100,
            type: 'number',
            renderCell: params => params.value ? (
                <Tooltip
                    title={t('controlled_by_user')}
                    placement="top"
                >
                    <Person
                        color="success"
                        fontSize="small"
                    />
                </Tooltip>
            ) : (
                <Tooltip
                    title={t('invite_manager')}
                    placement="top"
                >
                    <Hail
                        color="disabled"
                        fontSize="small"
                        onClick={() => openInviteManagerModal(params.row)
                            .then(party => setParties(old => replaceItemAtIndex(old, old.findIndex(i => i.id === party.id), party)))
                        }
                    />
                </Tooltip>
            ),
        }] as GridColDef<PartyModel>[] : [],
        {
            field: 'carsCount',
            headerName: t('cars'),
            width: 100,
            type: 'number',
            renderCell: params => (
                <Chip
                    sx={{cursor: 'pointer'}}
                    label={params.value}
                    color={params.value === 0 ? 'error' : (params.value > 1 ? 'info' : undefined)}
                    variant={params.value > 1 ? 'outlined' : undefined}
                    size="small"
                    component={Link}
                    to={`/cars/party/${params.row.id}`}
                />
            ),
        },
        {
            field: 'actions',
            type: 'actions',
            getActions: params => {
                return [
                    ...canEdit ? [
                    <GridActionsCellItem
                        icon={<Edit/>}
                        label={t('edit')}
                        onClick={() => openEditPartyModal(params.row)
                            .then(party => setParties(old => replaceItemAtIndex(old, old.findIndex(i => i.id === party.id), party)))
                        }
                    />] : [],
                    ...my.type === CompanyType.Master ? [
                        <GridActionsCellItem
                            icon={<Title/>}
                            label={t('change_type')}
                            onClick={() => openChangePartyTypeModal(params.row)
                                .then(() => setParties(old => removeItemAtIndex(old, old.findIndex(i => i.id === params.row.id))))
                            }
                            showInMenu
                        />,
                    ] : [],
                ];
            },
        },
    ];

    return (
        <Box sx={{height: 'calc(100vh - 148px)', bgcolor: 'background.paper'}}>
            {selectionModel.length > 1 && (
                <Box sx={{position: 'absolute', right: 0, top: -24,}}>
                    <Button
                        size="small"
                        onClick={() => openMergePartiesModal(parties.filter(p => selectionModel.includes(p.id)))
                            .then(() => setSelectionModel([]))
                            .then(load)}
                    >
                        {t('merge')}
                    </Button>
                </Box>
            )}
            <DataGrid
                pageSizeOptions={[100]}
                rows={parties}
                columns={columns}
                checkboxSelection={my.type === CompanyType.Master}
                disableRowSelectionOnClick
                rowSelectionModel={selectionModel}
                onRowSelectionModelChange={setSelectionModel}
                filterModel={id ? {
                    items: [{ field: 'id', operator: '=', value: id }],
                } : undefined}
            />
        </Box>
    );
};

const Table = (p: TableProps) => {
    return (
        <Suspense fallback={null}>
            <TableInner {...p} />
        </Suspense>
    )
}

export default Table;
